import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getDocumentName } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { describeErrors } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { userForm2ApiMapper } from '../mappers/user.form2api.mapper';
import type { UserFormTab } from '../enums/tab.enum';
import type { UserFormValues } from '../types/user-form-values.type';

import { useCreateUserMutation } from './useCreateUserMutation.hook';
import { UserForm_UsersPersonIds_Query } from './useUsersPersonIdsQuery.hook';

export type CreatedUser = Readonly<{ userId: Id; password: string }>;

export const useUserSubmit = (onCreated: (created: CreatedUser) => void) => {
  const queryClient = useQueryClient();
  const { mutate: createUserMutation } = useCreateUserMutation();

  const handleSubmit = useCallback(
    ({
      values,
      formikHelpers: { setSubmitting },
      setWizardError,
    }: Readonly<WizardSubmitHandlerParams<UserFormValues, UserFormTab>>) => {
      setWizardError(undefined);

      createUserMutation(userForm2ApiMapper(values), {
        onSuccess: ({ createUser }) => {
          void queryClient.invalidateQueries({
            queryKey: [getDocumentName(UserForm_UsersPersonIds_Query)],
          });
          // Still submitting: leaving the saved form for the user page asks
          // no confirmation.
          onCreated({
            userId: createUser.user._id,
            password: createUser.password,
          });
        },
        onError: (error) => {
          setWizardError({ messages: describeErrors(error) });
          setSubmitting(false);
        },
      });

      return undefined;
    },
    [createUserMutation, onCreated, queryClient],
  );

  return { handleSubmit };
};
